import { useState, useEffect, useCallback } from "react";
import {
  Users,
  DollarSign,
  Target,
  TrendingUp,
  Wallet,
  Play,
  Loader2,
  RefreshCw,
  Copy,
  Check,
  Activity,
} from "lucide-react";
import "./index.css";
import "./wallet-status.css"; // 👈 NEW styles for pills + progress bar
import { runMeowFiAnalytics } from "./meowfi-analytics";

// Cache constants
const CACHE_KEY = 'meowfi_analytics_cache';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

const MeowFiDashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [visibleCount, setVisibleCount] = useState(10); // lazy-load wallets
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Cache management functions
  const getCachedData = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        if (now - timestamp < CACHE_EXPIRY) {
          return data;
        }
        // Cache expired, remove it
        localStorage.removeItem(CACHE_KEY);
      }
    } catch (error) {
      console.warn('Error reading cache:', error);
    }
    return null;
  }, []);

  const setCachedData = (data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Error setting cache:', error);
    }
  };

  // Load cached data on component mount
  useEffect(() => {
    const cachedData = getCachedData();
    if (cachedData) {
      setAnalytics(cachedData);
      setLastFetchTime(new Date().toLocaleTimeString());
    }
  }, [getCachedData]);

  const runAnalyticsScript = async (forceRefresh = false) => {
    // If we have cached data and not forcing refresh, show it immediately
    const cachedData = getCachedData();
    if (cachedData && !forceRefresh) {
      setAnalytics(cachedData);
      setLastFetchTime(new Date().toLocaleTimeString());
      setIsRefreshing(true); // Show refreshing state while fetching new data
    } else {
      setIsLoading(true);
    }
    
    setError(null);
    setVisibleCount(10); // reset lazy-load when re-running
    
    try {
      const result = await runMeowFiAnalytics();
      setAnalytics(result);
      setCachedData(result); // Cache the new data
      setLastFetchTime(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err.message || "Failed to run analytics. Please try again.");
      // If we had cached data, keep showing it despite the error
      if (!cachedData) {
        setAnalytics(null);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue", loading = false }) => {
    return (
      <div className={`stat-card stat-card-${color} animate-fadeIn`}>
        <div className="stat-card-header">
          <div className={`stat-icon stat-icon-${color}`}>
            <Icon size={20} />
          </div>
          <div className="stat-title">{title}</div>
        </div>
        <div className="stat-content">
          <div className="stat-value">
            {loading ? (
              <div className="stat-loading">
                <span className="skeleton skeleton--title w-28" />
                <span className="skeleton-text">Loading…</span>
              </div>
            ) : (
              value
            )}
          </div>
          {subtitle && <div className="stat-subtitle">{subtitle}</div>}
        </div>
      </div>
    );
  };

  const WalletTableRow = ({
    wallet,
    valueUSD,
    positionCount,
    activeCount = 0,
    inactiveCount = 0,
    index,
    totalValue,
    dustValue,
    apr,
    loading = false,
  }) => {
    const [copied, setCopied] = useState(false);
    const truncateAddress = (addr) => `${addr.slice(0, 6)}...${addr.slice(-4)}`;

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(wallet);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        const textArea = document.createElement("textarea");
        textArea.value = wallet;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand("copy");
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (fallbackErr) {
          // ignore
        }
        document.body.removeChild(textArea);
      }
    };

    return (
      <div className="table-row">
        {/* Desktop/Tablet Table Layout */}
        <div className="table-cell smart-account" data-label="Account">
          <div className="flex items-center gap-3">
            <div className="wallet-index">{loading ? <span className="skeleton skeleton--badge" /> : index + 1}</div>
            <div className="flex items-center gap-2">
              <span className="wallet-address">
                {loading ? <span className="skeleton skeleton--text w-24" /> : truncateAddress(wallet)}
              </span>
              <button onClick={copyToClipboard} className="copy-btn" title="Copy address">
                {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
              </button>
            </div>
          </div>
        </div>
        
        <div className="table-cell active-positions" data-label="Active">
          {loading ? <span className="skeleton skeleton--text w-8" /> : activeCount}
        </div>
        
        <div className="table-cell inactive-positions" data-label="Inactive">
          {loading ? <span className="skeleton skeleton--text w-8" /> : inactiveCount}
        </div>
        
        <div className="table-cell position-balance" data-label="Position">
          {loading ? (
            <span className="skeleton skeleton--text w-20" />
          ) : (
            <span className="position-balance-amount">${valueUSD ? valueUSD.toFixed(2) : '0.00'}</span>
          )}
        </div>
        
        <div className="table-cell total-balance" data-label="Total">
          {loading ? (
            <span className="skeleton skeleton--text w-20" />
          ) : (
            <div className="balance-display">
              <span className="balance-amount">${totalValue ? totalValue.toFixed(2) : '0.00'}</span>
              <span className="balance-dust"> (${dustValue ? dustValue.toFixed(2) : '0.00'})</span>
            </div>
          )}
        </div>

        <div className="table-cell apr-cell" data-label="APR">
          {loading ? (
            <span className="skeleton skeleton--text w-20" />
          ) : (
            <span className="position-balance-amount">{apr ? apr.toFixed(2): '0.00'}%</span>
          )}
        </div>

      </div>
    );
  };

  return (
    <div className="app-container">
      <div className="app-content">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-content">
            <div className="header-icon">
              <Activity size={20} />
            </div>
            <div className="header-text">
              <h1 className="header-title">Alloc8 Analytics</h1>
              <p className="header-subtitle">Real-time position analytics for smart wallets</p>
            </div>
          </div>
          
          <div className="header-actions">
            {analytics && lastFetchTime && (
              <div className="cache-info">
                <span className="cache-time" style={{
					visibility:"hidden"
				}}>Last updated: {lastFetchTime}</span>
                {isRefreshing && (
                  <span className="refreshing-indicator" style={{
					visibility:"hidden"
				}}>
                    <Loader2 className="button-spinner" size={12} />
                    Refreshing...
                  </span>
                )}
              </div>
            )}
            
            <div className="action-buttons">
              <button
                onClick={() => runAnalyticsScript(false)}
                disabled={isLoading}
                className="analytics-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="button-spinner" size={16} />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    <span>{analytics ? 'Run Analytics Query': 'Refresh Data' }</span>
                  </>
                )}
              </button>
              
              {analytics && (
                <button
                  onClick={() => runAnalyticsScript(true)}
                  disabled={isLoading || isRefreshing}
                  className="refresh-button"
                  title="Force refresh (ignore cache)"
                >
                  <RefreshCw size={16} className={isRefreshing ? 'button-spinner' : ''} />
                </button>
              )}
              <button
                onClick={() => window.open("https://app.alloc8.xyz", "_blank")}
                style={{
                  background: '#9AFB01',
                  color: 'black',
                  border: '2px solid #9AFB01',
                  padding: '0.5rem 1.25rem',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  // textTransform: 'uppercase',
                  letterSpacing: '1px',
                  // boxShadow: '0 0 10px #0ff, 0 0 20px #0ff, 0 0 30px #0ff',
                  // textShadow: '0 0 5px #0ff, 0 0 10px #0ff',
                  // animation: 'pulse 2s infinite',
                }}
              >
                Launch Alloc8
              </button>

            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-card mb-8 text-center">
            <p className="font-bold">Error:</p>
            <p>{error}</p>
          </div>
        )}

        {/* Inline Data/Skeleton Rendering */}
        {(analytics || isLoading) && (
          <div className="space-y-8 animate-fadeIn" aria-busy={isLoading} aria-live="polite">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
			<StatCard
                icon={DollarSign}
                title="Total Asset under Agent"
                value={analytics ? `$${analytics.totalTVL.toFixed(2)}` : ''}
                subtitle={analytics ? `${analytics.activeTVLPercent.toFixed(2)}% of TVL earning fees` : ''}
                color="pink"
                loading={isLoading}
              />
              {/* <StatCard
                icon={Users}
                title="Smart Wallets"
                value={analytics ? analytics.totalSmartWallets : ''}
                subtitle="Total wallets analyzed"
                color="blue"
                loading={isLoading}
              /> */}
              <StatCard
                icon={Wallet}
                title="Total # of Users "
                value={analytics ? analytics.walletsWithPositionsAbove10 : ''}
                subtitle="Wallets with total holdings > $10"
                color="green"
                loading={isLoading}
              />
              <StatCard
                icon={Target}
                title="Total # of Agents"
                value={analytics ? analytics.totalPositionsFromQualifiedWallets : ''}
                subtitle="Total positions from wallets > $10"
                color="purple"
                loading={isLoading}
              />
              <StatCard
                icon={TrendingUp}
                title="Avg Position"
                value={analytics ? `$${analytics.averagePositionValueFromQualifiedWallets.toFixed(2)}` : ''}
                subtitle="Average for all positions from qualified wallets"
                color="orange"
                loading={isLoading}
              />

            </div>

            {/* Qualifying Wallets Table */}
            <div className="wallet-table-container">
              <div className="table-header">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="table-header-icon">
                      <Wallet size={24} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-1">Qualifying Wallets</h2>
                      <p className="text-gray-400 text-sm">Wallets with positions worth $10 or more</p>
                    </div>
                  </div>
                  {!isLoading && analytics && (
                    <div className="table-stats">
                      <span className="stat-badge">{analytics.qualifyingWallets.length} wallets</span>
                    </div>
                  )}
                </div>
                {isLoading && (
                  <div className="loading-indicator">
                    <div className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <p className="text-gray-500 text-sm ml-3">Fetching wallets and positions…</p>
                  </div>
                )}
              </div>
              
              <div className="data-table">
                <div className="table-header-row">
                  <div className="table-header-cell">Smart Account</div>
                  <div className="table-header-cell">Active Positions</div>
                  <div className="table-header-cell">Inactive Positions</div>
                  {/* <div className="table-header-cell">Total Positions</div> */}
                  <div className="table-header-cell">Position Balance</div>
                  <div className="table-header-cell">Total Value (Balance)</div>
                  <div className="table-header-cell">APR</div>
                </div>
                
                <div className="table-body">
                  {isLoading
                    ? Array.from({ length: 8 }).map((_, idx) => (
                        <WalletTableRow
                          key={`skeleton-${idx}`}
                          wallet={''}
                          valueUSD={0}
                          positionCount={0}
                          activeCount={0}
                          inactiveCount={0}
                          index={idx}
                          totalValue={0}
                          dustValue={0}
                          apr={0}

                          loading={true}
                        />
                      ))
                    : analytics.qualifyingWallets
                        .slice(0, visibleCount)
                        .map((w, index) => (
                          <WalletTableRow
                            key={w.wallet}
                            wallet={w.wallet}
                            valueUSD={w.valueUSD}
                            positionCount={w.positionCount}
                            activeCount={w.activeCount}
                            inactiveCount={w.inactiveCount}
                            index={index}
                            totalValue={w.totalValueUSD}
                            dustValue={w.dustValueUSD}
                            apr={w.totalAPR}
                            loading={false}
                          />
                        ))}
                </div>
              </div>
              
              {!isLoading && analytics.qualifyingWallets.length > visibleCount && (
                <div className="text-center mt-6">
                  <button
                    className="btn-load-more"
                    onClick={() => setVisibleCount((c) => c + 10)}
                    aria-label="Load more wallets"
                  >
                    Load more ({Math.max(analytics.qualifyingWallets.length - visibleCount, 0)} remaining)
                  </button>
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="summary-container">
              <div className="summary-header">
                <h3 className="summary-title">Analytics Summary</h3>
                <p className="summary-subtitle">Key insights from qualifying wallets</p>
              </div>
              
              <div className="summary-grid">
                <div className="summary-item">
                  <div className="summary-label">Qualifying Wallets</div>
                  <div className="summary-value">
                    {isLoading ? (
                      <span className="skeleton skeleton--text w-16" />
                    ) : (
                      <>
                        <span className="summary-number">{analytics.walletsWithPositionsAbove10}</span>
                        <span className="summary-total">/ {analytics.totalSmartWallets}</span>
                      </>
                    )}
                  </div>
                  <div className="summary-description">Wallets with $10+ holdings</div>
                </div>

                <div className="summary-item">
                  <div className="summary-label">Total Positions</div>
                  <div className="summary-value">
                    {isLoading ? (
                      <span className="skeleton skeleton--text w-12" />
                    ) : (
                      <span className="summary-number">{analytics.totalPositionsFromQualifiedWallets}</span>
                    )}
                  </div>
                  <div className="summary-description">From qualifying wallets</div>
                </div>

                <div className="summary-item">
                  <div className="summary-label">Average Position</div>
                  <div className="summary-value">
                    {isLoading ? (
                      <span className="skeleton skeleton--text w-20" />
                    ) : (
                      <span className="summary-number">${analytics.averagePositionValueFromQualifiedWallets.toFixed(2)}</span>
                    )}
                  </div>
                  <div className="summary-description">Per position value</div>
                </div>

                <div className="summary-item">
                  <div className="summary-label">Total TVL</div>
                  <div className="summary-value">
                    {isLoading ? (
                      <span className="skeleton skeleton--text w-24" />
                    ) : (
                      <>
                        <span className="summary-number">${analytics.totalTVL.toFixed(2)}</span>
                        <span className="summary-percentage">({analytics.activeTVLPercent.toFixed(1)}% active)</span>
                      </>
                    )}
                  </div>
                  <div className="summary-description">Total value locked</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* End Inline Data/Skeleton Rendering */}
        {false && (
          <div className="space-y-8 animate-fadeIn">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <StatCard
                icon={Users}
                title="Smart Wallets"
                value={analytics.totalSmartWallets}
                subtitle="Total wallets analyzed"
                color="blue"
              />
              <StatCard
                icon={Wallet}
                title="Wallets > $10"
                value={analytics.walletsWithPositionsAbove10}
                subtitle="Wallets with total holdings > $10"
                color="green"
              />
              <StatCard
                icon={Target}
                title="Positions from qualified wallets"
                value={analytics.totalPositionsFromQualifiedWallets}
                subtitle="Total positions from wallets > $10"
                color="purple"
              />
              <StatCard
                icon={TrendingUp}
                title="Avg Position Value"
                value={`$${analytics.averagePositionValueFromQualifiedWallets.toFixed(
                  2
                )}`}
                subtitle="Average for all positions from qualified wallets"
                color="orange"
              />
              {/* Total TVL + % active (earning fees) */}
              <StatCard
                icon={DollarSign}
                title="Total TVL"
                value={`$${analytics.totalTVL.toFixed(2)}`}
                subtitle={`${analytics.activeTVLPercent.toFixed(
                  2
                )}% of TVL earning fees`}
                color="pink"
              />
            </div>

              <div>
                <h3 className="text-xl font-bold text-gray-300 mb-2">No Data Yet</h3>
                <p className="text-gray-500">
                  Click "Run Analytics Query" to start analyzing Alloc8 positions
                </p>
              </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default MeowFiDashboard;
